
import React, { useState, useRef } from 'react';
import { Camera, X } from 'lucide-react';
import Button from '../Button';
import { Product, Category } from '../../types';
import { readFileAsBase64 } from '../../utils/fileHelpers';

interface ProductFormModalProps {
    categories: Category[];
    onClose: () => void;
    onSubmit: (product: Product) => void;
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({ categories, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCatIds, setSelectedCatIds] = useState<string[]>([]);
  const [tags, setTags] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    if (!name || !image) return;
    onSubmit({
      id: Date.now().toString(),
      name,
      description,
      categoryIds: selectedCatIds,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      images: [{ id: Date.now().toString(), url: image, type: 'original', createdAt: Date.now() }],
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const base64 = await readFileAsBase64(file);
        setImage(base64);
      } catch (err) {
        console.error("Failed to read file");
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-md transition-opacity" onClick={onClose} />
      <div className="relative bg-white rounded-[2rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col">
        <div className="p-8 border-b border-stone-100 flex justify-between items-center sticky top-0 bg-white z-10">
            <h3 className="text-2xl font-serif font-bold text-stone-900">Add New Product</h3>
            <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><X className="w-5 h-5 text-stone-400" /></button>
        </div>
        
        <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                <div 
                className={`aspect-[3/4] rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-300 group ${image ? 'border-0' : 'border-2 border-dashed border-stone-200 hover:border-stone-400 hover:bg-stone-50'}`}
                onClick={() => fileRef.current?.click()}
                >
                {image ? (
                    <div className="relative w-full h-full">
                         <img src={image} className="w-full h-full object-cover rounded-2xl shadow-md" alt="Preview" />
                         <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                             <div className="bg-white px-3 py-1.5 rounded-full text-xs font-bold text-stone-900">Change Image</div>
                         </div>
                    </div>
                ) : (
                    <div className="text-center text-stone-400">
                    <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <Camera className="w-8 h-8 opacity-50 text-stone-500" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-wide">Upload Photo</span>
                    </div>
                )}
                </div>
                <input type="file" ref={fileRef} hidden onChange={handleFile} accept="image/*" />
            </div>

            <div className="space-y-6">
                <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Product Name</label>
                <input 
                    type="text" 
                    className="w-full p-3.5 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-stone-900 focus:outline-none transition-shadow font-medium"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Diamond Solitaire Ring"
                />
                </div>
                <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Description</label>
                <textarea 
                    className="w-full p-3.5 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-stone-900 focus:outline-none h-32 resize-none transition-shadow text-sm"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of the item..."
                />
                </div>
                <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Categories</label>
                <div className="flex flex-wrap gap-2">
                    {categories.map((cat: any) => (
                    <button
                        key={cat.id}
                        onClick={() => setSelectedCatIds(prev => prev.includes(cat.id) ? prev.filter(c => c !== cat.id) : [...prev, cat.id])}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-lg border transition-all ${selectedCatIds.includes(cat.id) ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'}`}
                    >
                        {cat.name}
                    </button>
                    ))}
                </div>
                </div>
                <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Tags</label>
                <input 
                    type="text" 
                    placeholder="gold, diamond, summer..."
                    className="w-full p-3.5 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-stone-900 focus:outline-none transition-shadow text-sm"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                />
                </div>
            </div>
            </div>
        </div>

        <div className="p-6 border-t border-stone-100 flex justify-end gap-3 bg-stone-50 rounded-b-[2rem]">
            <Button variant="ghost" onClick={onClose} size="lg">Cancel</Button>
            <Button onClick={handleSubmit} disabled={!name || !image} size="lg" className="shadow-lg shadow-stone-900/10">Create Product</Button>
        </div>
      </div>
    </div>
  );
};

export default ProductFormModal;
